import React, { useEffect, useState } from "react";
import { Layer, Stage, Image, Group } from "react-konva";
import ReactResizeDetector from "react-resize-detector";

import { ISize } from "types";
import { IWithballData } from "apis";
import footballPitch from "assets/img/football_pitch.jpg";
import {
  EVENT_VALUES,
  TACKLE_METHOD,
} from "modules/withball-data-input/constants";

import { StatsPass } from "./StatsPass";
import { StatsClearance } from "./StatsClearance";
import { StatsDribble } from "./StatsDribble";
import { StatsTackle } from "./StatsTackle";
import { StatsInterception } from "./StatsInterception";
import { StatsFoul } from "./StatsFoul";
import { StatsCard } from "./StatsCard";
import { StatsShoot } from "./StatsShoot";

interface IStatisticsBoardProps {
  data: IWithballData[];
}

const STAGE_RATIO = 1.487;

const calculateHeight = (width: number) => {
  return width / STAGE_RATIO;
};

export const StatisticsBoard = ({ data }: IStatisticsBoardProps) => {
  const [backgroundImage, setBackgroundImage] = useState<any>(null);
  useEffect(() => {
    if (!backgroundImage) {
      const image = new window.Image();
      image.src = footballPitch;
      image.onload = () => {
        setBackgroundImage(image);
      };
    }
  }, [backgroundImage]);

  return (
    <ReactResizeDetector handleWidth>
      {({ width }: ISize) => {
        const height = calculateHeight(width) || 0;
        return (
          <Stage width={width} height={height}>
            <Layer>
              {backgroundImage && (
                <Image width={width} height={height} image={backgroundImage} />
              )}
              <Group>
                {data.map((each) => {
                  switch (each.action) {
                    case EVENT_VALUES.PASS:
                    case EVENT_VALUES.THROUGH:
                    case EVENT_VALUES.CORNER_KICK:
                    case EVENT_VALUES.FREE_KICK:
                      return (
                        <StatsPass
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          to={{
                            x: (width || 0) * each.toX,
                            y: (height || 0) * each.toY,
                          }}
                          result={each.succeed}
                          threat={each.threat}
                        />
                      );
                    case EVENT_VALUES.CLEARANCE:
                      return (
                        <StatsClearance
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          result={each.succeed}
                        />
                      );
                    case EVENT_VALUES.DRIBBLE:
                      return (
                        <StatsDribble
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          result={each.succeed}
                        />
                      );
                    case EVENT_VALUES.TACKLE:
                      return (
                        <StatsTackle
                          key={each.id}
                          type={
                            each.method === TACKLE_METHOD.normal
                              ? "normal"
                              : "sliding"
                          }
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          result={each.succeed}
                        />
                      );
                    case EVENT_VALUES.INTERCEPTION:
                      return (
                        <StatsInterception
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          result={each.succeed}
                        />
                      );
                    case EVENT_VALUES.FOUL:
                      return (
                        <StatsFoul
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                        />
                      );
                    case EVENT_VALUES.OFFSIDE:
                      return (
                        <StatsFoul
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          symbol="OFF"
                        />
                      );
                    case EVENT_VALUES.YELLOW:
                      return (
                        <StatsCard
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          type="yellow"
                        />
                      );
                    case EVENT_VALUES.RED:
                      return (
                        <StatsCard
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          type="red"
                        />
                      );
                    case EVENT_VALUES.PENALTY:
                    case EVENT_VALUES.SHOOT:
                      return (
                        <StatsShoot
                          key={each.id}
                          from={{
                            x: (width || 0) * each.fromX,
                            y: (height || 0) * each.fromY,
                          }}
                          to={{
                            x: (width || 0) * each.toX,
                            y: (height || 0) * each.toY,
                          }}
                          result={each.succeed}
                          threat={each.threat}
                        />
                      );
                    default:
                      return null;
                  }
                })}
              </Group>
            </Layer>
          </Stage>
        );
      }}
    </ReactResizeDetector>
  );
};
